import React, { useState, useEffect } from "react";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TablePagination,
  TableRow, Container, Typography, Avatar, Chip, IconButton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, Tooltip,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { collection, getDocs, doc, updateDoc, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";
import Scrollbar from "../components/Scrollbar";
import { TableTitle, TableHeader } from "../components/_dashboard/user";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TABLE_HEAD = [
  { id: "reportedUser", label: "Reported User", alignRight: false },
  { id: "violation", label: "Violation", alignRight: false },
  { id: "reportedBy", label: "Reported By", alignRight: false },
  { id: "date", label: "Date", alignRight: false },
  { id: "status", label: "Status", alignRight: false },
  { id: "actions", label: "Actions", alignRight: true },
];

const VIOLATION_COLORS = {
  spam: "warning",
  harassment: "error",
  "hate speech": "error",
  "false info": "warning",
  "market manipulation": "error",
  other: "default",
};

export default function ReportedUsers() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("date");
  const [noteDialog, setNoteDialog] = useState({ open: false, report: null, note: "" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, report: null });

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "Reports"));
      setReports(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      toast.error("Failed to load reports");
    }
    setLoading(false);
  };

  const handleSuspend = async (report) => {
    try {
      const suspendUntil = new Date();
      suspendUntil.setDate(suspendUntil.getDate() + 7);
      // Find and update the reported user
      const usersSnap = await getDocs(collection(db, "User"));
      const userDoc = usersSnap.docs.find(
        (d) => d.data().username === report.reportedUser || d.data().uid === report.reportedUid
      );
      if (userDoc) {
        await updateDoc(doc(db, "User", userDoc.id), {
          suspended: true,
          suspendedUntil: suspendUntil.toISOString(),
          status: false,
          violations: (userDoc.data().violations || 0) + 1,
        });
      }
      // Update report status
      await updateDoc(doc(db, "Reports", report.id), {
        status: "actioned",
        actionTaken: "7-day suspension",
        actionedAt: serverTimestamp(),
      });
      toast.success(`${report.reportedUser} suspended for 7 days`);
      loadReports();
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
    setConfirmDialog({ open: false, action: null, report: null });
  };

  const handleDismiss = async (report) => {
    try {
      await updateDoc(doc(db, "Reports", report.id), {
        status: "dismissed",
        actionedAt: serverTimestamp(),
      });
      toast.success("Report dismissed");
      loadReports();
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
    setConfirmDialog({ open: false, action: null, report: null });
  };

  const handleSaveNote = async () => {
    const { report, note } = noteDialog;
    try {
      await updateDoc(doc(db, "Reports", report.id), { adminNote: note });
      toast.success("Note saved");
      setNoteDialog({ open: false, report: null, note: "" });
      loadReports();
    } catch (err) {
      toast.error("Failed");
    }
  };

  const sorted = [...reports].sort((a, b) => {
    if (orderBy === "date") {
      const aT = a.createdAt?.seconds || a.timestamp?.seconds || 0;
      const bT = b.createdAt?.seconds || b.timestamp?.seconds || 0;
      return order === "desc" ? bT - aT : aT - bT;
    }
    return 0;
  });

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const formatDate = (report) => {
    const ts = report.createdAt || report.timestamp;
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString() + " " + d.toLocaleTimeString();
  };

  return (
    <Page title="Reported Users | Fire Investing Admin">
      <ToastContainer position="top-right" />
      <Container maxWidth="xl">
        <Box sx={{ pb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Reported Users</Typography>
          <Typography variant="body2" color="text.secondary">
            {reports.filter((r) => !r.status || r.status === "pending").length} pending · {reports.length} total
          </Typography>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
          <Scrollbar>
            <TableContainer>
              <Table>
                <TableTitle title="Community Reports" numSelected={0} />
                <TableHeader
                  order={order} orderBy={orderBy}
                  headLabel={TABLE_HEAD} rowCount={reports.length} numSelected={0}
                  onRequestSort={(_, prop) => {
                    setOrder(orderBy === prop && order === "asc" ? "desc" : "asc");
                    setOrderBy(prop);
                  }}
                />
                <TableBody>
                  {paginated.map((report) => {
                    const status = report.status || "pending";
                    const violation = (report.violation || report.reason || "other").toLowerCase();
                    return (
                      <TableRow key={report.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar src={report.reportedUserPhoto} sx={{ width: 36, height: 36 }}>
                              {(report.reportedUser || "?").charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {report.reportedUser || report.reportedUsername || "—"}
                              </Typography>
                              {report.adminNote && (
                                <Typography variant="caption" color="text.secondary">Note: {report.adminNote}</Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={violation}
                            size="small"
                            color={VIOLATION_COLORS[violation] || "default"}
                            sx={{ textTransform: "capitalize" }}
                          />
                          {report.text && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, maxWidth: 200 }}>
                              "{report.text?.substring(0, 60)}{report.text?.length > 60 ? "..." : ""}"
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar src={report.reportedByPhoto} sx={{ width: 28, height: 28 }}>
                              {(report.reportedBy || "?").charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2">{report.reportedBy || "—"}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(report)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={status}
                            size="small"
                            color={status === "pending" ? "warning" : status === "actioned" ? "success" : "default"}
                            sx={{ textTransform: "capitalize" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Suspend 7 days from community">
                            <span>
                              <IconButton
                                size="small" color="error"
                                disabled={status !== "pending"}
                                onClick={() => setConfirmDialog({ open: true, action: "suspend", report })}
                              >
                                <Icon icon="eva:slash-fill" width={18} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Dismiss report">
                            <span>
                              <IconButton
                                size="small" color="success"
                                disabled={status !== "pending"}
                                onClick={() => setConfirmDialog({ open: true, action: "dismiss", report })}
                              >
                                <Icon icon="eva:checkmark-circle-fill" width={18} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Add admin note">
                            <IconButton
                              size="small"
                              onClick={() => setNoteDialog({ open: true, report, note: report.adminNote || "" })}
                            >
                              <Icon icon="eva:message-square-outline" width={18} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {paginated.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          {loading ? "Loading reports..." : "No reports found"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div" count={sorted.length} rowsPerPage={rowsPerPage} page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Card>
      </Container>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, report: null })} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmDialog.action === "suspend" ? "Suspend User" : "Dismiss Report"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmDialog.action === "suspend"
              ? `Suspend "${confirmDialog.report?.reportedUser}" for 7 days from the community? Their violation count will increase.`
              : `Dismiss report against "${confirmDialog.report?.reportedUser}"? No action will be taken.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, report: null })}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmDialog.action === "suspend" ? "error" : "success"}
            onClick={() => confirmDialog.action === "suspend"
              ? handleSuspend(confirmDialog.report)
              : handleDismiss(confirmDialog.report)
            }
          >
            {confirmDialog.action === "suspend" ? "Suspend 7 Days" : "Dismiss"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteDialog.open} onClose={() => setNoteDialog({ open: false, report: null, note: "" })} maxWidth="sm" fullWidth>
        <DialogTitle>Admin Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth multiline rows={3} label="Note"
            value={noteDialog.note}
            onChange={(e) => setNoteDialog((p) => ({ ...p, note: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog({ open: false, report: null, note: "" })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNote}>Save Note</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
